<template>
  <div class="preview-page">
    <!-- 顶部工具栏 -->
    <div class="preview-header">
      <div class="header-left">
        <n-text strong style="font-size: 18px;">
          {{ projectName || '未命名项目' }}
        </n-text>
        <n-text depth="3" style="font-size: 14px; margin-left: 12px;">
          预览模式 (只读)
        </n-text>
      </div>

      <div class="header-center">
        <n-text depth="3" style="font-size: 14px;">
          已发布版本: {{ publishedVersion ?? '未发布' }}
        </n-text>
      </div>

      <div class="header-right">
        <n-button @click="goToEditor" type="primary">
          <template #icon>
            <n-icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </n-icon>
          </template>
          编辑此项目
        </n-button>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="preview-content">
      <!-- 3D 视口 -->
      <div class="viewport-container" ref="viewportRef">
        <!-- 加载状态 -->
        <div v-if="isLoading" class="viewport-placeholder">
          <n-spin size="large" />
          <n-text depth="3" style="margin-top: 16px;">
            {{ loadingMessage }}
          </n-text>
        </div>

        <!-- 未发布提示 -->
        <div v-else-if="!hasPublishedVersion" class="viewport-placeholder">
          <n-empty description="此项目尚未发布">
            <template #extra>
              <n-button @click="goToEditor" type="primary">
                前往编辑器发布
              </n-button>
            </template>
          </n-empty>
        </div>

        <!-- 错误提示 -->
        <div v-else-if="error" class="viewport-placeholder">
          <n-result status="error" title="加载失败" :description="error">
            <template #footer>
              <n-button @click="reload">重新加载</n-button>
            </template>
          </n-result>
        </div>
      </div>

      <!-- 视口工具栏 -->
      <div v-if="isRendererReady" class="viewport-toolbar">
        <n-button-group size="small">
          <n-button @click="handleResetView">
            <template #icon>
              <n-icon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 0 0 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                </svg>
              </n-icon>
            </template>
            重置视图
          </n-button>
          <n-button @click="toggleGrid">
            <template #icon>
              <n-icon>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM8 20H4v-4h4v4zm0-6H4v-4h4v4zm0-6H4V4h4v4zm6 12h-4v-4h4v4zm0-6h-4v-4h4v4zm0-6h-4V4h4v4zm6 12h-4v-4h4v4zm0-6h-4v-4h4v4zm0-6h-4V4h4v4z"/>
                </svg>
              </n-icon>
            </template>
            网格: {{ showGrid ? '开' : '关' }}
          </n-button>
        </n-button-group>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  NButton,
  NButtonGroup,
  NIcon,
  NText,
  NSpin,
  NEmpty,
  NResult,
  useMessage
} from 'naive-ui';
import { Viewer, type CadCommand } from '@puppycad/cad-core';

import { editorPreferencesRepo } from '@/services/db';
import { CommandRepository } from '@/services/db/command-repo';
import { pickOpenProjectRouteQuery } from '@/router/open-project-route-context';
import { restorePublishedPreviewModel } from '@/views/preview/restorePublishedPreviewModel';

const route = useRoute();
const router = useRouter();
const message = useMessage();

// Repositories
const commandRepo = new CommandRepository();

// 基础状态
const projectId = computed(() => route.params.projectId as string);
const projectName = ref('');
const publishedVersion = ref<number>();
const hasPublishedVersion = computed(() => publishedVersion.value !== undefined);

// 加载状态
const isLoading = ref(true);
const loadingMessage = ref('加载项目信息...');
const error = ref('');

// 视口状态
const viewportRef = ref<HTMLElement>();
const isRendererReady = ref(false);
const showGrid = ref(true);
let threeRenderer: Viewer | null = null;

// 返回编辑器
const goToEditor = () => {
  router.push({
    name: 'editor',
    params: {
      projectId: projectId.value,
    },
    query: pickOpenProjectRouteQuery(route.query),
  });
};

// 重新加载
const reload = () => {
  window.location.reload();
};

// 加载项目发布信息
async function loadProject(): Promise<boolean> {
  try {
    const preferences = await editorPreferencesRepo.get(projectId.value);
    projectName.value = projectId.value;
    publishedVersion.value = preferences?.publishedVersion;
    return true;
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : '未知错误';
    error.value = errMsg;
    return false;
  }
}

// 加载已发布的命令
async function loadPublishedCommands(): Promise<CadCommand[]> {
  try {
    if (!publishedVersion.value) {
      return [];
    }

    console.log(`加载已发布的命令 (版本号 <= ${publishedVersion.value})...`);

    // 使用 getCommandRange 获取已发布的命令
    const publishedCommands = await commandRepo.getCommandRange(
      projectId.value,
      0,
      publishedVersion.value
    );

    console.log(`已加载 ${publishedCommands.length} 条已发布命令`);
    return publishedCommands;

  } catch (err) {
    console.log('加载命令失败:', err);
    throw err;
  }
}

/**
 * 在完整文档 runtime 边界中重建预览模型。
 *
 * 设计说明：
 * 1. Preview 首屏加载是完整 CAD 文档恢复，不能直接调用旧 active Worker batch replay；
 * 2. 空命令代表已发布空模型，也要进入 full-document runtime switch 清空旧 Worker runtime；
 * 3. begin/replay/commit/abort 顺序由 cad-core 深入口负责，web-client 只传递项目上下文。
 *
 * @param commands 已发布版本命令历史。
 */
async function rebuildModel(commands: CadCommand[]) {
  try {
    if (!threeRenderer) {
      throw new Error('3D渲染器未初始化');
    }

    loadingMessage.value = commands.length > 0
      ? `正在回放命令历史 (${commands.length} 条)...`
      : '正在重置空白已发布模型...';
    await restorePublishedPreviewModel({
      viewer: threeRenderer,
      projectId: projectId.value,
      publishedVersion: publishedVersion.value,
      commands,
    });

    console.log('模型重建完成');

  } catch (err) {
    console.log('模型重建失败:', err);
    throw err;
  }
}

// 初始化3D渲染器
async function initRenderer() {
  try {
    if (!viewportRef.value) {
      throw new Error('视口容器未找到');
    }

    console.log('初始化3D渲染器...');

    // 创建并初始化THREE.js渲染器
    threeRenderer = new Viewer(viewportRef.value, {
      backgroundColor: 0x2a2a2a,
      gridSize: 0.01,
      gridDivisions: 0.05,
      cameraPosition: [0.1,0.1,0.1],
    });
    await threeRenderer.whenReady();

    isRendererReady.value = true;
    console.log('3D渲染器初始化成功');

  } catch (err) {
    console.log('3D渲染器初始化失败:', err);
    throw err;
  }
}

// 重置视图
function handleResetView() {
  console.log('重置视图');
  threeRenderer?.resetCamera();
}

// 切换网格显示
function toggleGrid() {
  showGrid.value = !showGrid.value;
  console.log(`网格显示: ${showGrid.value}`);
  threeRenderer?.setGridVisible(showGrid.value);
}

// 主加载流程
async function loadPreview() {
  try {
    isLoading.value = true;
    error.value = '';

    // 1. 加载项目发布信息
    const loaded = await loadProject();
    if (!loaded || !hasPublishedVersion.value) {
      isLoading.value = false;
      return;
    }

    // 2. 初始化3D渲染器
    await initRenderer();

    // 3. 加载已发布的命令
    const commands = await loadPublishedCommands();

    // 4. 批量执行命令重建模型
    await rebuildModel(commands);

    isLoading.value = false;
    message.success('模型加载成功');

  } catch (err) {
    const errMsg = err instanceof Error ? err.message : '加载失败';
    error.value = errMsg;
    isLoading.value = false;
    message.error(errMsg);
  }
}

// 组件挂载
onMounted(async () => {
  await loadPreview();
});

// 组件卸载
onUnmounted(() => {
  // 清理THREE.js资源
  threeRenderer?.dispose();
  threeRenderer = null;
  console.log('预览页面卸载');
});
</script>

<style scoped>
.preview-page {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  overflow: hidden;
}

/* 顶部工具栏 */
.preview-header {
  height: 60px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  flex-shrink: 0;
}

.header-left,
.header-center,
.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-center {
  flex: 1;
  justify-content: center;
}

/* 主内容区 */
.preview-content {
  flex: 1;
  display: flex;
  position: relative;
  overflow: hidden;
  background: #2a2a2a;
}

.viewport-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.viewport-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #888;
}

.viewport-toolbar {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
  padding: 8px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
</style>
